import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import DisclaimerFooter from '../components/DisclaimerFooter';
import { faqSummary, letter } from '../data/reimburseTmmsFaqData';
// Styles are shared with the other FAQ pages so all of them stay visually
// identical. ReimburseTmmsFaq.css is imported second and holds only this page's
// additions (the letter and its signatory list).
import './ChcaFaq.css';
import './ReimburseTmmsFaq.css';

const BASE_PATH = '/2026-stm2-reimburse-tmms-faq';

// Warrant Article 5's FAQ. Unlike the other FAQ pages this one has no questions
// yet, so its second tab is the petitioner's July 1, 2026 letter to the Select
// Board rather than a Q&A accordion. Nothing here deep-links to a question, so
// there are no permanent question ids to preserve — that starts when questions
// are added.
function ReimburseTmmsFaq() {
  const location = useLocation();
  const navigate = useNavigate();

  const getTabFromPath = useCallback(() => {
    const path = location.pathname.replace(new RegExp(`^${BASE_PATH}/?`), '');
    const parts = path.split('/').filter(Boolean);
    if (parts[0] === 'letter') return 'letter';
    return 'info';
  }, [location.pathname]);

  const [activeTab, setActiveTabState] = useState(getTabFromPath());

  // Keep state in step with back/forward navigation.
  useEffect(() => {
    setActiveTabState(getTabFromPath());
  }, [location.pathname, getTabFromPath]);

  const setActiveTab = useCallback((tab) => {
    setActiveTabState(tab);
    navigate(tab === 'letter' ? `${BASE_PATH}/letter` : `${BASE_PATH}/overview`);
  }, [navigate]);

  useEffect(() => {
    document.title = 'Reimbursing Town Meeting Members FAQ';
  }, []);

  // Renders **bold** and blank-line paragraph breaks. The source document uses
  // neither bullets nor numbered lists, so this stays deliberately small.
  const formatText = (text) =>
    text.split('\n\n').map((para, i) => (
      <p
        key={i}
        dangerouslySetInnerHTML={{
          __html: para.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>'),
        }}
      />
    ));

  return (
    <div className="chca-faq-page">
      <a href="#main-content" className="chca-skip-link">Skip to main content</a>
      <Header
        title="Reimbursing Town Meeting Members FAQ"
        subtitle="Warrant Article 5 - Fall 2026 Special Town Meeting"
        creditExtra="Content provided by Petitioner Catherine Anderson, website design by Michael Rubenstein"
      >
        <div className="chca-header-tabs">
          <button
            className={`chca-header-tab ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
            aria-pressed={activeTab === 'info'}
          >
            Overview
          </button>
          <button
            className={`chca-header-tab ${activeTab === 'letter' ? 'active' : ''}`}
            onClick={() => setActiveTab('letter')}
            aria-pressed={activeTab === 'letter'}
          >
            {letter.tabLabel}
          </button>
        </div>
      </Header>

      <main id="main-content" tabIndex={-1} className="faq-content">

        {/* ===== OVERVIEW PANEL ===== */}
        {activeTab === 'info' && (
          <div className="faq-panel">
            <section className="summary-section">
              <div className="version-badge">{faqSummary.version} - {faqSummary.date}</div>
              <div className="summary-text">
                {formatText(faqSummary.intro)}
              </div>
            </section>
          </div>
        )}

        {/* ===== LETTER PANEL ===== */}
        {activeTab === 'letter' && (
          <div className="faq-panel">
            <section className="letter-section" aria-labelledby="letter-heading">
              <h2 id="letter-heading" className="letter-heading">{letter.heading}</h2>

              <div className="letter-meta">
                {letter.meta.map((line, i) => <p key={i}>{line}</p>)}
              </div>

              <div className="letter-body">
                <p className="letter-salutation">{letter.salutation}</p>
                {letter.paragraphs.map((para, i) => <p key={i}>{para}</p>)}
                <p className="letter-closing">{letter.closing}</p>
              </div>

              <div className="letter-signatories">
                <h3 className="letter-signatories-heading">
                  Signed by {letter.signatoryCount} Town Meeting members
                </h3>
                <ul className="letter-signatory-list">
                  {letter.signatories.map((s) => (
                    <li key={`${s.name}-${s.precinct}`}>
                      {s.name}
                      <span className="letter-precinct">Precinct {s.precinct}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </div>
        )}

        {faqSummary.disclaimer && (
          <DisclaimerFooter>
            <div dangerouslySetInnerHTML={{ __html: faqSummary.disclaimer }} />
          </DisclaimerFooter>
        )}
      </main>
    </div>
  );
}

export default ReimburseTmmsFaq;
